"use client";

import { motion } from "framer-motion";
import { 
  Lightbulb, 
  Rocket, 
  Users, 
  TrendingUp,
  Brain,
  Zap,
  Target,
  CheckCircle
} from "lucide-react";
import SectionHeading from "@/components/SectionHeading";

interface JourneyStep {
  year: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  achievements: string[];
  color: string;
}

const journeySteps: JourneyStep[] = [
  {
    year: "2018",
    title: "The Spark",
    description: "Started as a software developer, but quickly realized the real problem wasn't building features—it was watching teams drown in manual work.",
    icon: <Lightbulb className="w-6 h-6" />,
    achievements: [
      "First full-stack application deployed",
      "Witnessed teams spending 60% time on manual tasks",
      "Identified automation as the key to human potential"
    ],
    color: "from-blue-500 to-cyan-500"
  },
  {
    year: "2020",
    title: "The Deep Dive",
    description: "Immersed myself in AI and machine learning. Built my first automation that saved a logistics company 40 hours per week.",
    icon: <Brain className="w-6 h-6" />,
    achievements: [
      "Mastered Python, ML frameworks",
      "First AI automation: 40 hours/week saved",
      "Realized the transformative power of intelligent systems"
    ],
    color: "from-purple-500 to-pink-500"
  },
  {
    year: "2022",
    title: "The Breakthrough",
    description: "Developed my signature approach: agentic AI systems that don&apos;t just automate—they think, adapt, and improve business processes.",
    icon: <Zap className="w-6 h-6" />,
    achievements: [
      "Created first agentic AI workflow",
      "Achieved 99.9% accuracy in automated processes",
      "Developed reusable automation frameworks"
    ],
    color: "from-orange-500 to-red-500"
  },
  {
    year: "2024",
    title: "The Mission",
    description: "Now helping SMEs worldwide transform their operations. Every automation I build returns thousands of hours to human teams.",
    icon: <Target className="w-6 h-6" />,
    achievements: [
      "80+ workflows automated across industries",
      "250K+ hours returned to teams",
      "6.5x average ROI for clients"
    ],
    color: "from-green-500 to-emerald-500"
  }
];

const coreValues = [
  {
    icon: <Users className="w-8 h-8 text-accent" />,
    title: "Human-Centric AI",
    description: "Technology should amplify human creativity, not replace it. Every system I build is designed to give people more time for meaningful work."
  },
  {
    icon: <TrendingUp className="w-8 h-8 text-accent" />,
    title: "Measurable Impact",
    description: "I don&apos;t just build cool tech—I deliver results. Every automation comes with clear ROI metrics and performance guarantees."
  },
  {
    icon: <Rocket className="w-8 h-8 text-accent" />,
    title: "Future-Ready Solutions",
    description: "My systems are built to evolve. They learn, adapt, and improve over time, ensuring your investment grows with your business."
  }
];

const PersonalJourney = () => {
  return (
    <section id="journey" className="py-24 px-6 bg-gradient-to-b from-background to-secondary/30">
      <div className="container max-w-6xl mx-auto">
        <SectionHeading title="The Journey: From Code to Impact" />
        
        {/* Story Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-xl text-muted-foreground leading-relaxed max-w-4xl mx-auto">
            Every great solution starts with a problem that keeps you up at night. 
            For me, it was watching brilliant people waste their potential on mind-numbing tasks. 
            Here&apos;s how I turned that frustration into a mission.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative mb-20">
          {/* Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-accent/20 via-accent/40 to-accent/20 rounded-full" />
          
          {journeySteps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`relative flex items-center mb-16 ${
                index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'
              }`}
            >
              {/* Content Card */}
              <div className={`w-5/12 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}>
                <div className="bg-card border border-border rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-r ${step.color} text-white`}>
                      {step.icon}
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{step.year}</div>
                      <div className="text-lg font-semibold text-accent">{step.title}</div>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {step.description}
                  </p>
                  
                  <div className="space-y-2">
                    {step.achievements.map((achievement, achievementIndex) => (
                      <div key={achievementIndex} className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Timeline Node */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 bg-accent rounded-full border-4 border-background shadow-lg z-10" />
              
              {/* Spacer */}
              <div className="w-5/12" />
            </motion.div>
          ))}
        </div>

        {/* Core Values */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl font-bold mb-4">What Drives Me</h3>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            These aren&apos;t just values—they&apos;re the principles that guide every line of code I write 
            and every system I design.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {coreValues.map((value, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className="bg-card border border-border rounded-2xl p-6 text-center hover:shadow-lg transition-all duration-300"
            >
              <div className="mb-4 flex justify-center">
                <div className="p-4 bg-accent/10 rounded-2xl">
                  {value.icon}
                </div>
              </div>
              <h4 className="text-xl font-semibold mb-3">{value.title}</h4>
              <p className="text-muted-foreground leading-relaxed">{value.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-accent/10 via-primary/5 to-accent/10 rounded-3xl p-8 border border-accent/20">
            <h3 className="text-2xl font-bold mb-4">Ready to Write Your Success Story?</h3>
            <p className="text-lg text-muted-foreground mb-6">
              Let&apos;s transform your manual processes into intelligent systems that work while you sleep.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-3 bg-accent text-accent-foreground rounded-xl font-semibold hover:bg-accent/90 transition-colors"
              onClick={() => document.getElementById('impact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              See the Impact in Action
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PersonalJourney; 